import React, { ReactNode, useContext, useState } from "react";
import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AppContext } from "context/appContext";
import { AUTURA_ASPHALT_LIGHT } from "context/themeContext";
import SignaturePad from "components/inputs/SignaturePad";

interface DigitalSignatureWidgetProps {
  onSignatureApplied?: (applied: boolean) => void;

}

const DigitalSignatureWidget = ({ onSignatureApplied  }: DigitalSignatureWidgetProps): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const [isSignatureApplied, setIsSignatureApplied] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  const hasUserSignature = state.signature !== null;

  const handleApplySignature = () => {
    setIsSignatureApplied(true);
    if (onSignatureApplied) {
      onSignatureApplied(true);
    }
  };

  const handleRemoveSignature = () => {
    setIsSignatureApplied(false);
    if (onSignatureApplied) {
      onSignatureApplied(false);
    }
  };

  const handleCreateSignature = () => {
    setShowSignaturePad(true);
  };

  const handleSignatureCreated = () => {
    setShowSignaturePad(false);
    // After creating signature, automatically apply it
    setIsSignatureApplied(true);
    if (onSignatureApplied) {
      onSignatureApplied(true);
    }
  };

  // Show SignaturePad if user doesn't have a signature or explicitly wants to create one
  if (!hasUserSignature || showSignaturePad) {
    return (
      <Box sx={{ width: "100%" }}>
        <SignaturePad onSignatureCreated={handleSignatureCreated} />
        {showSignaturePad && (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setShowSignaturePad(false)}
              size="small"
            >
              {t("cancel")}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>      
      {!isSignatureApplied ? (
        // Show appropriate button based on whether user has a signature
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {hasUserSignature ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleApplySignature}
                sx={{ minWidth: 120 }}
              >
                {t("signature.apply", { ns: "userSettings" })}
              </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCreateSignature}
              startIcon={<Edit />}
            >
              {t("signature.create_new", { ns: "userSettings" })}
            </Button>
          )}
        </Box>
      ) : (
        <Card sx={{ border: `1px solid ${AUTURA_ASPHALT_LIGHT}`, position: "relative", maxWidth: 400 }}>
          <IconButton
            size="small"
            onClick={handleRemoveSignature}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Box sx={{ p: 0 }}>
            <img
              src={state.signature?.url || ""}
              alt="Applied signature"
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: 120,
                display: "block",
              }}
            />
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default DigitalSignatureWidget;